import {
  CHILD_MEAL_BANDS,
  RATE_SEASONS,
  childMealsProblem,
  type ChildMealPrices,
  type RateProfile,
  type RateSeason,
} from '@lemontaigu/pms-core';

// « Importer un profil » — reads a profile authored as JSON (the shape of
// `RateProfile`, amounts in cents) and checks it field by field before the
// screen hands it to `createProfileDraft`. Nothing is written here: a parse
// failure must leave the screen exactly as it was.

export type ProfileImport = { ok: true; profile: RateProfile; note: string | null } | { ok: false; error: string };

const isInt = (v: unknown, min: number): v is number =>
  typeof v === 'number' && Number.isInteger(v) && v >= min;

const isFraction = (v: unknown): v is number => typeof v === 'number' && v >= 0 && v <= 1;

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

function seasonRecord(v: unknown, what: string, min: number): Record<RateSeason, number> | string {
  if (!isRecord(v)) return `${what} : objet attendu.`;
  const out = {} as Record<RateSeason, number>;
  for (const s of RATE_SEASONS) {
    if (!isInt(v[s], min)) return `${what}.${s} : entier ≥ ${min} attendu.`;
    out[s] = v[s] as number;
  }
  return out;
}

function mealPair(v: unknown, what: string): { breakfast: number; dinner: number } | string {
  if (!isRecord(v)) return `${what} : objet attendu.`;
  if (!isInt(v.breakfast, 0)) return `${what}.breakfast : entier ≥ 0 attendu (centimes).`;
  if (!isInt(v.dinner, 0)) return `${what}.dinner : entier ≥ 0 attendu (centimes).`;
  return { breakfast: v.breakfast, dinner: v.dinner };
}

/** Parse and validate an imported profile document. Unknown keys (such as a
 *  `_note` or `status`) are ignored: the profile is rebuilt from known fields
 *  only, so a stray key can never reach the database. */
export function parseProfileImport(text: string): ProfileImport {
  let data: unknown;
  try {
    data = JSON.parse(text);
  } catch {
    return { ok: false, error: 'Fichier JSON invalide.' };
  }
  if (!isRecord(data)) return { ok: false, error: 'Le fichier doit contenir un objet JSON.' };

  if (typeof data.name !== 'string' || data.name.trim() === '') {
    return { ok: false, error: 'Champ « name » manquant.' };
  }

  if (!isRecord(data.roomTypes)) return { ok: false, error: 'roomTypes : objet attendu.' };
  const roomTypes: RateProfile['roomTypes'] = {};
  for (const [id, rt] of Object.entries(data.roomTypes)) {
    if (!/^\d+$/.test(id)) return { ok: false, error: `roomTypes « ${id} » : identifiant numérique attendu.` };
    if (!isRecord(rt)) return { ok: false, error: `roomTypes.${id} : objet attendu.` };
    const markup = seasonRecord(rt.markup, `roomTypes.${id}.markup`, 0);
    if (typeof markup === 'string') return { ok: false, error: markup };
    if (rt.minPosted !== null && !isInt(rt.minPosted, 0)) {
      return { ok: false, error: `roomTypes.${id}.minPosted : entier ≥ 0 ou null attendu.` };
    }
    roomTypes[Number(id)] = { markup, minPosted: rt.minPosted ?? null } as RateProfile['roomTypes'][number];
  }

  const booking = mealPair(data.mealsBooking, 'mealsBooking');
  if (typeof booking === 'string') return { ok: false, error: booking };
  const direct = mealPair(data.mealsDirect, 'mealsDirect');
  if (typeof direct === 'string') return { ok: false, error: direct };

  // Optional (PB1): absent or null = children at the adult direct price.
  let childMealsDirect: ChildMealPrices | null = null;
  if (data.childMealsDirect != null) {
    const problem = childMealsProblem(data.childMealsDirect);
    if (problem) return { ok: false, error: problem };
    const src = data.childMealsDirect as ChildMealPrices;
    // Rebuilt from known fields, like everything else here.
    childMealsDirect = Object.fromEntries(
      CHILD_MEAL_BANDS.map((b) => [b, { breakfast: src[b].breakfast, dinner: src[b].dinner }]),
    ) as ChildMealPrices;
  }

  if (!Array.isArray(data.lengthOfStayTiers)) {
    return { ok: false, error: 'lengthOfStayTiers : liste attendue.' };
  }
  const tiers = data.lengthOfStayTiers.map((t, i) => {
    if (!isRecord(t)) return `lengthOfStayTiers[${i}] : objet attendu.`;
    if (!isInt(t.minNights, 1)) return `lengthOfStayTiers[${i}].minNights : entier ≥ 1 attendu.`;
    if (!isFraction(t.discountStay)) return `lengthOfStayTiers[${i}].discountStay : fraction entre 0 et 1 attendue.`;
    if (!isFraction(t.discountMeals)) return `lengthOfStayTiers[${i}].discountMeals : fraction entre 0 et 1 attendue.`;
    return { minNights: t.minNights, discountStay: t.discountStay, discountMeals: t.discountMeals };
  });
  const badTier = tiers.find((t): t is string => typeof t === 'string');
  if (badTier) return { ok: false, error: badTier };

  const minStay = seasonRecord(data.minStayBySeason, 'minStayBySeason', 1);
  if (typeof minStay === 'string') return { ok: false, error: minStay };

  const note = typeof data.note === 'string' && data.note.trim() !== '' ? data.note.trim() : null;

  return {
    ok: true,
    note,
    profile: {
      name: data.name.trim(),
      roomTypes,
      mealsBooking: booking,
      mealsDirect: direct,
      ...(childMealsDirect === null ? {} : { childMealsDirect }),
      lengthOfStayTiers: tiers as RateProfile['lengthOfStayTiers'],
      minStayBySeason: minStay,
    },
  };
}
